import { buildChildURL } from '@centric-os/helpers';
import type { RouteRecordRaw } from 'vue-router';
import { LoyaltyRouteNames } from '../enums/router-names';
import { LoyaltyRoutePathNames } from '../enums/url-paths';
import { beforeEnter, beforeEnterBundlesHidden } from './guards/index';

const CampaignList = () => import('../views/campaigns/CampaignList.vue');
const CampaignCreate = () => import('../views/campaigns/CampaignCreate.vue');
const CampaignDetail = () => import('../views/campaigns/CampaignDetail.vue');

const VoucherList = () => import('../views/vouchers/VoucherList.vue');
const VoucherCreate = () => import('../views/vouchers/VoucherCreate.vue');
const VoucherDetail = () => import('../views/vouchers/VoucherDetail.vue');

const RewardList = () => import('../views/rewards/RewardList.vue');
const RewardCreate = () => import('../views/rewards/RewardCreate.vue');
const RewardDetail = () => import('../views/rewards/RewardDetail.vue');
const RewardCardDetail = () => import('../views/rewards/RewardCardDetail.vue');

const SegmentList = () => import('../views/segments/SegmentList.vue');
const SegmentCreate = () => import('../views/segments/SegmentCreate.vue');
const SegmentDetail = () => import('../views/segments/SegmentDetail.vue');

const BundleList = () => import('../views/bundles/BundleList.vue');
const BundleCreate = () => import('../views/bundles/BundleCreate.vue');
const BundleDetail = () => import('../views/bundles/BundleDetail.vue');
const BundleEdit = () => import('../views/bundles/BundleEdit.vue');

const ReportingCampaigns = () => import('../views/reporting/ReportingCampaigns.vue');
const ReportingCustomers = () => import('../views/reporting/ReportingCustomers.vue');

// --- Campaigns ---

const campaignListRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.CAMPAIGNS, LoyaltyRoutePathNames.LIST),
  name: LoyaltyRouteNames.CAMPAIGNS_LIST,
  component: CampaignList,
  beforeEnter,
};

const campaignCreateRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.CAMPAIGNS, LoyaltyRoutePathNames.CREATE),
  name: LoyaltyRouteNames.CAMPAIGNS_CREATE,
  component: CampaignCreate,
  beforeEnter,
};

const campaignDetailRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.CAMPAIGNS, LoyaltyRoutePathNames.DETAIL),
  name: LoyaltyRouteNames.CAMPAIGNS_DETAIL,
  component: CampaignDetail,
  beforeEnter,
};

const campaignEditRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.CAMPAIGNS, LoyaltyRoutePathNames.EDIT),
  name: LoyaltyRouteNames.CAMPAIGNS_EDIT,
  component: CampaignCreate,
  beforeEnter,
};

// --- Vouchers ---

const voucherListRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.VOUCHERS, LoyaltyRoutePathNames.LIST),
  name: LoyaltyRouteNames.VOUCHERS_LIST,
  component: VoucherList,
  beforeEnter,
};

const voucherCreateRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.VOUCHERS, LoyaltyRoutePathNames.CREATE),
  name: LoyaltyRouteNames.VOUCHERS_CREATE,
  component: VoucherCreate,
  beforeEnter,
};

const voucherDetailRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.VOUCHERS, LoyaltyRoutePathNames.DETAIL),
  name: LoyaltyRouteNames.VOUCHERS_DETAIL,
  component: VoucherDetail,
  beforeEnter,
};

// --- Rewards ---

const rewardListRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.REWARDS, LoyaltyRoutePathNames.LIST),
  name: LoyaltyRouteNames.REWARDS_LIST,
  component: RewardList,
  beforeEnter,
};

const rewardCreateRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.REWARDS, LoyaltyRoutePathNames.CREATE),
  name: LoyaltyRouteNames.REWARDS_CREATE,
  component: RewardCreate,
  beforeEnter,
};

const rewardDetailRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.REWARDS, LoyaltyRoutePathNames.DETAIL),
  name: LoyaltyRouteNames.REWARDS_DETAIL,
  component: RewardDetail,
  beforeEnter,
};

const rewardEditRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.REWARDS, LoyaltyRoutePathNames.EDIT),
  name: LoyaltyRouteNames.REWARDS_EDIT,
  component: RewardCreate,
  beforeEnter,
};

const rewardCardDetailRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.REWARDS, LoyaltyRoutePathNames.CARD_DETAIL),
  name: LoyaltyRouteNames.REWARDS_CARD_DETAIL,
  component: RewardCardDetail,
  beforeEnter,
};

// --- Segments ---

const segmentListRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.SEGMENTS, LoyaltyRoutePathNames.LIST),
  name: LoyaltyRouteNames.SEGMENTS_LIST,
  component: SegmentList,
  beforeEnter,
};

const segmentCreateRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.SEGMENTS, LoyaltyRoutePathNames.CREATE),
  name: LoyaltyRouteNames.SEGMENTS_CREATE,
  component: SegmentCreate,
  beforeEnter,
};

const segmentDetailRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.SEGMENTS, LoyaltyRoutePathNames.DETAIL),
  name: LoyaltyRouteNames.SEGMENTS_DETAIL,
  component: SegmentDetail,
  beforeEnter,
};

// --- Bundles (hidden from promo prod scope — CGE-1286) ---

const bundleListRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.BUNDLES, LoyaltyRoutePathNames.LIST),
  name: LoyaltyRouteNames.BUNDLES_LIST,
  component: BundleList,
  beforeEnter: beforeEnterBundlesHidden,
};

const bundleCreateRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.BUNDLES, LoyaltyRoutePathNames.CREATE),
  name: LoyaltyRouteNames.BUNDLES_CREATE,
  component: BundleCreate,
  beforeEnter: beforeEnterBundlesHidden,
};

const bundleDetailRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.BUNDLES, LoyaltyRoutePathNames.DETAIL),
  name: LoyaltyRouteNames.BUNDLES_DETAIL,
  component: BundleDetail,
  beforeEnter: beforeEnterBundlesHidden,
};

const bundleEditRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.BUNDLES, LoyaltyRoutePathNames.EDIT),
  name: LoyaltyRouteNames.BUNDLES_EDIT,
  component: BundleEdit,
  beforeEnter: beforeEnterBundlesHidden,
};

// --- Reporting ---

const reportingCampaignsRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.REPORTING, LoyaltyRoutePathNames.CAMPAIGNS),
  name: LoyaltyRouteNames.REPORTING_CAMPAIGNS,
  component: ReportingCampaigns,
  beforeEnter,
};

const reportingCustomersRoute: RouteRecordRaw = {
  path: buildChildURL(LoyaltyRoutePathNames.REPORTING, LoyaltyRoutePathNames.CUSTOMERS),
  name: LoyaltyRouteNames.REPORTING_CUSTOMERS,
  component: ReportingCustomers,
  beforeEnter,
};

const childRoutes: RouteRecordRaw[] = [
  campaignListRoute,
  campaignCreateRoute,
  campaignEditRoute,
  campaignDetailRoute,

  voucherListRoute,
  voucherCreateRoute,
  voucherDetailRoute,

  rewardListRoute,
  rewardCreateRoute,
  rewardEditRoute,
  rewardDetailRoute,
  rewardCardDetailRoute,

  segmentListRoute,
  segmentCreateRoute,
  segmentDetailRoute,

  bundleListRoute,
  bundleCreateRoute,
  bundleEditRoute,
  bundleDetailRoute,

  reportingCampaignsRoute,
  reportingCustomersRoute,
];

export default childRoutes;
